"use client";

import React, { useEffect, useRef, useState } from "react";

type XcutiqStudioComponentContext = {
  component?: string | undefined;
  module?: string | undefined;
  file?: string | undefined;
  domPath?: string | undefined;
  text?: string | undefined;
  classes?: string | undefined;
};

export const XCUTIQ_STUDIO_BRIDGE_PROTOCOL = "xcutiq-studio-bridge/v1";

type XcutiqStudioTargetBridgeProps = {
  enabled?: boolean;
  controllerUrl?: string;
  targetId?: string;
};

type PanelBounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type PanelInteraction = {
  kind: "drag" | "n" | "e" | "s" | "w" | "ne" | "nw" | "se" | "sw";
  pointerId: number;
  startX: number;
  startY: number;
  bounds: PanelBounds;
};

type BridgeMessage = {
  protocol: typeof XCUTIQ_STUDIO_BRIDGE_PROTOCOL;
  type:
    | "controller-probe"
    | "controller-show"
    | "controller-close"
    | "target-reload"
    | "target-ready"
    | "picker-start"
    | "picker-cancel"
    | "selection";
  targetId: string;
  session: string;
  pathname?: string;
  href?: string;
  context?: XcutiqStudioComponentContext;
};

const BRIDGE_SESSION_KEY = "xcutiq-studio:external-bridge-session:v1";
const PANEL_OPEN_KEY = "xcutiq-studio:external-panel-open:v1";
const PANEL_BOUNDS_KEY = "xcutiq-studio:external-panel-bounds:v1";
const SELECTABLE_TARGETS =
  "[data-codex-component], [data-xcutiq-component], button, a, input, textarea, select, section, main, nav, aside, header, footer, article, form, div";

export function XcutiqStudioTargetBridge({
  enabled = process.env.NODE_ENV === "development",
  controllerUrl = "https://studio.xcutiq.com",
  targetId = "default",
}: XcutiqStudioTargetBridgeProps) {
  const [pickerActive, setPickerActive] = useState(false);
  const [panelMounted, setPanelMounted] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [controllerFrameUrl, setControllerFrameUrl] = useState("");
  const [panelBounds, setPanelBounds] = useState<PanelBounds>();
  const controllerWindowRef = useRef<Window | null>(null);
  const controllerFrameRef = useRef<HTMLIFrameElement | null>(null);
  const sessionRef = useRef("");
  const panelInteractionRef = useRef<PanelInteraction | undefined>(undefined);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const resolvedControllerUrl = resolvePeerUrl(controllerUrl);
    const controllerOrigin = new URL(resolvedControllerUrl).origin;
    const session = readOrCreateBridgeSession();
    sessionRef.current = session;
    const url = new URL(resolvedControllerUrl);
    url.searchParams.set("target", targetId);
    url.searchParams.set("bridgeSession", session);
    url.searchParams.set("embedded", "1");
    const initialization = window.setTimeout(() => {
      setPanelBounds(readPanelBounds());
      setControllerFrameUrl(url.toString());
      if (window.localStorage.getItem(PANEL_OPEN_KEY) === "true") {
        setPanelMounted(true);
        setPanelOpen(true);
      }
    }, 0);

    const sendReady = (target?: MessageEventSource | null) => {
      const recipient = asWindow(target) ?? controllerWindowRef.current;
      recipient?.postMessage(
        createBridgeMessage("target-ready", targetId, session, {
          pathname: window.location.pathname,
          href: window.location.href,
        }),
        controllerOrigin,
      );
    };

    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== controllerOrigin || !isBridgeMessage(event.data)) {
        return;
      }
      if (event.data.targetId !== targetId || event.data.session !== session) {
        return;
      }
      const source = asWindow(event.source);
      if (source) {
        controllerWindowRef.current = source;
      }
      if (event.data.type === "controller-probe") {
        sendReady(event.source);
      } else if (event.data.type === "controller-close") {
        setPanelOpen(false);
        window.localStorage.setItem(PANEL_OPEN_KEY, "false");
      } else if (event.data.type === "target-reload") {
        window.location.reload();
      } else if (event.data.type === "picker-start") {
        setPickerActive(true);
        setPanelOpen(false);
        window.focus();
      } else if (event.data.type === "picker-cancel") {
        setPickerActive(false);
        setPanelOpen(true);
        window.localStorage.setItem(PANEL_OPEN_KEY, "true");
      }
    };

    let shortcutLatched = false;
    const onKeyDown = (event: KeyboardEvent) => {
      const isShortcut =
        event.ctrlKey &&
        event.altKey &&
        (event.code === "KeyD" || event.key.toLowerCase() === "d");
      if (!isShortcut || shortcutLatched || event.repeat) {
        return;
      }
      shortcutLatched = true;
      event.preventDefault();
      event.stopImmediatePropagation();
      setPanelMounted(true);
      setPanelOpen((current) => {
        const next = !current;
        window.localStorage.setItem(PANEL_OPEN_KEY, String(next));
        if (next) {
          window.setTimeout(() => {
            const controllerWindow = controllerFrameRef.current?.contentWindow ?? null;
            controllerWindowRef.current = controllerWindow;
            sendReady(controllerWindow);
            controllerWindow?.postMessage(
              createBridgeMessage("controller-show", targetId, session),
              controllerOrigin,
            );
          }, 50);
        }
        return next;
      });
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (
        event.code === "KeyD" ||
        event.key.toLowerCase() === "d" ||
        event.key === "Control" ||
        event.key === "Alt"
      ) {
        shortcutLatched = false;
      }
    };

    window.addEventListener("message", onMessage);
    document.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("keyup", onKeyUp, true);
    return () => {
      window.clearTimeout(initialization);
      window.removeEventListener("message", onMessage);
      document.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("keyup", onKeyUp, true);
    };
  }, [controllerUrl, enabled, targetId]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const onPointerMove = (event: PointerEvent) => {
      const interaction = panelInteractionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) {
        return;
      }
      const deltaX = event.clientX - interaction.startX;
      const deltaY = event.clientY - interaction.startY;
      setPanelBounds(
        resizeOrMovePanel(interaction.bounds, interaction.kind, deltaX, deltaY),
      );
    };
    const finishInteraction = (event: PointerEvent) => {
      const interaction = panelInteractionRef.current;
      if (!interaction || event.pointerId !== interaction.pointerId) {
        return;
      }
      panelInteractionRef.current = undefined;
      setPanelBounds((current) => {
        if (current) {
          window.localStorage.setItem(PANEL_BOUNDS_KEY, JSON.stringify(current));
        }
        return current;
      });
    };
    const onResize = () => {
      setPanelBounds((current) => clampPanelBounds(current ?? defaultPanelBounds()));
    };

    window.addEventListener("pointermove", onPointerMove, true);
    window.addEventListener("pointerup", finishInteraction, true);
    window.addEventListener("pointercancel", finishInteraction, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("pointermove", onPointerMove, true);
      window.removeEventListener("pointerup", finishInteraction, true);
      window.removeEventListener("pointercancel", finishInteraction, true);
      window.removeEventListener("resize", onResize);
    };
  }, [enabled]);

  const startPanelInteraction = (
    kind: PanelInteraction["kind"],
    event: React.PointerEvent<HTMLElement>,
  ) => {
    if (!panelBounds || event.button !== 0) {
      return;
    }
    panelInteractionRef.current = {
      kind,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      bounds: panelBounds,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  useEffect(() => {
    if (!enabled || !pickerActive) {
      return;
    }

    const resolvedControllerUrl = resolvePeerUrl(controllerUrl);
    const controllerOrigin = new URL(resolvedControllerUrl).origin;
    const marker = document.createElement("div");
    marker.dataset.xcutiqStudioBridge = "picker-marker";
    Object.assign(marker.style, {
      display: "none",
      position: "absolute",
      zIndex: "2147483646",
      border: "2px solid #f5c84c",
      borderRadius: "8px",
      background: "rgba(245, 200, 76, 0.12)",
      pointerEvents: "none",
      boxSizing: "border-box",
    });
    document.body.appendChild(marker);

    const findTarget = (eventTarget: EventTarget | null) => {
      const element = eventTarget instanceof Element ? eventTarget : null;
      const target = element?.closest(SELECTABLE_TARGETS);
      return target?.closest("[data-xcutiq-studio-bridge]") ? null : target;
    };
    const updateMarker = (target: Element) => {
      const rect = target.getBoundingClientRect();
      marker.style.display = "block";
      marker.style.left = `${rect.left + window.scrollX}px`;
      marker.style.top = `${rect.top + window.scrollY}px`;
      marker.style.width = `${rect.width}px`;
      marker.style.height = `${rect.height}px`;
    };
    const finish = () => setPickerActive(false);
    const onMove = (event: MouseEvent) => {
      const target = findTarget(event.target);
      if (target) {
        updateMarker(target);
      } else {
        marker.style.display = "none";
      }
    };
    const onClick = (event: MouseEvent) => {
      const target = findTarget(event.target);
      if (!target) {
        return;
      }
      event.preventDefault();
      event.stopImmediatePropagation();
      const element = target as HTMLElement;
      const context: XcutiqStudioComponentContext = {
        component:
          element.dataset.codexComponent ||
          element.dataset.xcutiqComponent ||
          readableName(element),
        module:
          element.dataset.codexModule ||
          element.dataset.xcutiqModule ||
          classifyModule(window.location.pathname),
        file: element.dataset.codexFile || element.dataset.xcutiqFile,
        domPath: domPath(element),
        text: element.innerText?.trim().slice(0, 240),
        classes: element.className?.toString().slice(0, 240),
      };
      controllerWindowRef.current?.postMessage(
        createBridgeMessage("selection", targetId, sessionRef.current, {
          pathname: window.location.pathname,
          href: window.location.href,
          context,
        }),
        controllerOrigin,
      );
      controllerWindowRef.current?.focus();
      setPanelOpen(true);
      window.localStorage.setItem(PANEL_OPEN_KEY, "true");
      finish();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }
      controllerWindowRef.current?.postMessage(
        createBridgeMessage("picker-cancel", targetId, sessionRef.current),
        controllerOrigin,
      );
      setPanelOpen(true);
      window.localStorage.setItem(PANEL_OPEN_KEY, "true");
      finish();
    };

    window.addEventListener("mousemove", onMove, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKeyDown, true);
    return () => {
      marker.remove();
      window.removeEventListener("mousemove", onMove, true);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKeyDown, true);
    };
  }, [controllerUrl, enabled, pickerActive, targetId]);

  if (!enabled) {
    return null;
  }

  return (
    <>
      {panelMounted && controllerFrameUrl && (
        <div
          data-xcutiq-studio-bridge="controller-panel"
          style={{
            position: "fixed",
            left: panelBounds?.x ?? "4vw",
            top: panelBounds?.y ?? "5vh",
            width: panelBounds?.width ?? "92vw",
            height: panelBounds?.height ?? "90vh",
            zIndex: 2147483645,
            display: panelOpen ? "block" : "none",
            overflow: "hidden",
            border: "2px solid #d5ad3c",
            borderRadius: 24,
            background: "#07111f",
            boxShadow: "0 24px 80px rgba(0,0,0,.72), 0 0 28px rgba(213,173,60,.28)",
          }}
        >
          <iframe
            ref={controllerFrameRef}
            src={controllerFrameUrl}
            title="XcutIQ StudIO controller"
            allow="clipboard-read; clipboard-write"
            onLoad={() => {
              const controllerWindow = controllerFrameRef.current?.contentWindow ?? null;
              controllerWindowRef.current = controllerWindow;
              const controllerOrigin = new URL(resolvePeerUrl(controllerUrl)).origin;
              controllerWindow?.postMessage(
                createBridgeMessage("target-ready", targetId, sessionRef.current, {
                  pathname: window.location.pathname,
                  href: window.location.href,
                }),
                controllerOrigin,
              );
            }}
            style={{ width: "100%", height: "100%", border: 0, display: "block" }}
          />
          <button
            type="button"
            aria-label="Drag StudIO panel"
            title="Drag StudIO panel"
            data-xcutiq-studio-drag-handle
            onPointerDown={(event) => startPanelInteraction("drag", event)}
            style={dragHandleStyle}
          >
            •••
          </button>
          {(["n", "e", "s", "w", "ne", "nw", "se", "sw"] as const).map(
            (direction) => (
              <button
                type="button"
                aria-label={`Resize StudIO panel ${direction}`}
                title="Resize StudIO panel"
                data-xcutiq-studio-resize-handle={direction}
                key={direction}
                onPointerDown={(event) => startPanelInteraction(direction, event)}
                style={resizeHandleStyle(direction)}
              />
            ),
          )}
        </div>
      )}
      {pickerActive && (
        <div
          data-xcutiq-studio-bridge="picker-banner"
          role="status"
          style={{
            position: "fixed",
            left: "50%",
            top: 16,
            zIndex: 2147483647,
            transform: "translateX(-50%)",
            border: "1px solid #f5c84c",
            borderRadius: 999,
            background: "#101822",
            color: "#f8e9b0",
            padding: "10px 18px",
            boxShadow: "0 10px 36px rgba(0,0,0,.45)",
            fontFamily: "system-ui, sans-serif",
            fontSize: 14,
          }}
        >
          Select any target area · Esc cancels
        </div>
      )}
    </>
  );
}

function createBridgeMessage(
  type: BridgeMessage["type"],
  targetId: string,
  session: string,
  fields: Partial<BridgeMessage> = {},
): BridgeMessage {
  return {
    protocol: XCUTIQ_STUDIO_BRIDGE_PROTOCOL,
    type,
    targetId,
    session,
    ...fields,
  };
}

function isBridgeMessage(value: unknown): value is BridgeMessage {
  if (!value || typeof value !== "object") {
    return false;
  }
  const message = value as Partial<BridgeMessage>;
  return (
    message.protocol === XCUTIQ_STUDIO_BRIDGE_PROTOCOL &&
    typeof message.type === "string" &&
    typeof message.targetId === "string" &&
    typeof message.session === "string"
  );
}

function asWindow(source: MessageEventSource | null | undefined): Window | null {
  return source && "closed" in source ? (source as Window) : null;
}

function resolvePeerUrl(template: string): string {
  const host = typeof window === "undefined" ? "localhost" : window.location.hostname;
  return template.replaceAll("{host}", host);
}

function readOrCreateBridgeSession(): string {
  const existing = window.sessionStorage.getItem(BRIDGE_SESSION_KEY);
  if (existing) {
    return existing;
  }
  const value = createBridgeId();
  window.sessionStorage.setItem(BRIDGE_SESSION_KEY, value);
  return value;
}

function createBridgeId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function readableName(element: HTMLElement): string {
  return (
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.innerText?.trim().split("\n")[0]?.slice(0, 80) ||
    element.tagName.toLowerCase()
  );
}

function domPath(element: Element): string {
  const path: string[] = [];
  let current: Element | null = element;
  while (current && path.length < 6) {
    let selector = current.tagName.toLowerCase();
    if (current.id) {
      selector += `#${CSS.escape(current.id)}`;
      path.unshift(selector);
      break;
    }
    const parent: Element | null = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter(
        (child: Element) => child.tagName === current?.tagName,
      );
      if (siblings.length > 1) {
        selector += `:nth-of-type(${siblings.indexOf(current) + 1})`;
      }
    }
    path.unshift(selector);
    current = parent;
  }
  return path.join(" > ");
}

function classifyModule(pathname: string): string {
  const normalized = pathname.replace(/^\/(ar|en)(?=\/|$)/, "") || "/";
  if (normalized === "/") {
    return "studio-fork";
  }
  return normalized.split("/").filter(Boolean).slice(0, 2).join("-") || "general";
}

const dragHandleStyle: React.CSSProperties = {
  position: "absolute",
  top: 5,
  left: "50%",
  zIndex: 3,
  width: 74,
  height: 22,
  transform: "translateX(-50%)",
  border: "1px solid rgba(213,173,60,.62)",
  borderRadius: 999,
  background: "rgba(7,17,31,.88)",
  color: "#d5ad3c",
  cursor: "grab",
  touchAction: "none",
  lineHeight: "14px",
  letterSpacing: 4,
};

function resizeHandleStyle(
  direction: Exclude<PanelInteraction["kind"], "drag">,
): React.CSSProperties {
  const base: React.CSSProperties = {
    position: "absolute",
    zIndex: 4,
    border: 0,
    padding: 0,
    background: "transparent",
    touchAction: "none",
  };
  if (direction === "n" || direction === "s") {
    return {
      ...base,
      [direction === "n" ? "top" : "bottom"]: 0,
      left: 18,
      right: 18,
      height: 8,
      cursor: "ns-resize",
    };
  }
  if (direction === "e" || direction === "w") {
    return {
      ...base,
      [direction === "e" ? "right" : "left"]: 0,
      top: 18,
      bottom: 18,
      width: 8,
      cursor: "ew-resize",
    };
  }
  return {
    ...base,
    top: direction.startsWith("n") ? 0 : undefined,
    bottom: direction.startsWith("s") ? 0 : undefined,
    left: direction.endsWith("w") ? 0 : undefined,
    right: direction.endsWith("e") ? 0 : undefined,
    width: 20,
    height: 20,
    cursor: direction === "nw" || direction === "se" ? "nwse-resize" : "nesw-resize",
  };
}

function readPanelBounds(): PanelBounds {
  try {
    const stored = JSON.parse(window.localStorage.getItem(PANEL_BOUNDS_KEY) || "null") as
      | Partial<PanelBounds>
      | null;
    if (
      stored &&
      [stored.x, stored.y, stored.width, stored.height].every(
        (value) => typeof value === "number" && Number.isFinite(value),
      )
    ) {
      return clampPanelBounds(stored as PanelBounds);
    }
  } catch {
    // Invalid local layout state falls back to governed defaults.
  }
  return defaultPanelBounds();
}

function defaultPanelBounds(): PanelBounds {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  return clampPanelBounds({
    x: Math.round(viewportWidth * 0.04),
    y: Math.round(viewportHeight * 0.05),
    width: Math.round(viewportWidth * 0.92),
    height: Math.round(viewportHeight * 0.9),
  });
}

function clampPanelBounds(bounds: PanelBounds): PanelBounds {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const minimumWidth = Math.min(760, Math.max(320, viewportWidth - 16));
  const minimumHeight = Math.min(520, Math.max(320, viewportHeight - 16));
  const width = clamp(bounds.width, minimumWidth, viewportWidth);
  const height = clamp(bounds.height, minimumHeight, viewportHeight);
  return {
    x: clamp(bounds.x, 0, Math.max(0, viewportWidth - width)),
    y: clamp(bounds.y, 0, Math.max(0, viewportHeight - height)),
    width,
    height,
  };
}

function resizeOrMovePanel(
  bounds: PanelBounds,
  kind: PanelInteraction["kind"],
  deltaX: number,
  deltaY: number,
): PanelBounds {
  if (kind === "drag") {
    return clampPanelBounds({
      ...bounds,
      x: bounds.x + deltaX,
      y: bounds.y + deltaY,
    });
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const minimumWidth = Math.min(760, Math.max(320, viewportWidth - 16));
  const minimumHeight = Math.min(520, Math.max(320, viewportHeight - 16));
  let left = bounds.x;
  let top = bounds.y;
  let right = bounds.x + bounds.width;
  let bottom = bounds.y + bounds.height;

  if (kind.includes("w")) {
    left = clamp(bounds.x + deltaX, 0, right - minimumWidth);
  }
  if (kind.includes("e")) {
    right = clamp(right + deltaX, left + minimumWidth, viewportWidth);
  }
  if (kind.includes("n")) {
    top = clamp(bounds.y + deltaY, 0, bottom - minimumHeight);
  }
  if (kind.includes("s")) {
    bottom = clamp(bottom + deltaY, top + minimumHeight, viewportHeight);
  }

  return { x: left, y: top, width: right - left, height: bottom - top };
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}
